import React, { useState } from "react";
import { Send, MessageCircleMore, Loader2, CheckCircle, AlertCircle } from "lucide-react";
import Container from "../components/Container";
import Breadcrumb from "../components/Breadcrumb";
import { cls } from "../utils/helpers";
import { WHATSAPP_NUM } from "../utils/constants";
import { apiClient } from "../utils/apiClient";

function openWhatsApp(preset) {
  const num = WHATSAPP_NUM.replace(/\D/g, "");
  const text = encodeURIComponent(
    preset ||
      "Bonjour, je souhaite échanger au sujet de vos prestations / software."
  );
  window.open(`https://wa.me/${num}?text=${text}`, "_blank");
}

export default function ContactPage({ p, t }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [company, setCompany] = useState("");
  const [subject, setSubject] = useState("presta");
  const [msg, setMsg] = useState("");
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  async function submitContact(e) {
    e.preventDefault();

    if (!name || !email || !phone || !company || !msg) {
      setError(t("form.error.required") || "Please fill in all required fields.");
      return;
    }

    if (!consent) {
      setError(t("form.error.consent") || "Please accept the privacy policy.");
      return;
    }

    setLoading(true);
    setError("");
    setSuccess(false);

    const subjectTxt =
      subject === "presta"
        ? t("form.subject.presta")
        : subject === "module"
        ? t("form.subject.module")
        : t("form.subject.other");

    const result = await apiClient.sendContactForm({
      name,
      email,
      phone,
      company,
      message: msg,
      subject: subjectTxt,
    });

    setLoading(false);

    if (result.success) {
      setSuccess(true);
      setName("");
      setEmail("");
      setPhone("");
      setCompany("");
      setMsg("");
      setConsent(false);
      setSubject("presta");
    } else {
      if (result.status === 429) {
        setError(t("form.error.rate_limit") || "Too many requests. Please try again in an hour.");
      } else {
        setError(result.error || t("form.error.generic") || "An error occurred. Please try again.");
      }
    }
  }

  return (
    <>
      <Container>
        <Breadcrumb
          items={[{ label: t("crumb.home"), href: "/" }, { label: t("nav.contact") }]}
          p={p}
        />
      </Container>
      <section className={p.hero}>
        <Container>
          <div className="py-12">
            <h1 className={cls("text-3xl font-extrabold", p.cardTitle)}>
              {t("contact.title")}
            </h1>
            <p className={cls("mt-2 max-w-3xl", p.paragraph)}>
              {t("contact.subtitle")}
            </p>
          </div>
        </Container>
      </section>

      <section className="py-10">
        <Container>
          <form
            onSubmit={submitContact}
            className={cls("grid gap-4 md:grid-cols-2 backdrop-blur rounded-2xl p-6", p.formContainer)}
          >
            <div>
              <label className="text-sm font-semibold">{t("form.name")} *</label>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                className={cls("mt-1 w-full rounded-xl p-3", p.formInput)}
                placeholder={t("form.placeholder.name")}
                required
                disabled={loading}
              />
            </div>
            <div>
              <label className="text-sm font-semibold">{t("form.email")} *</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={cls("mt-1 w-full rounded-xl p-3", p.formInput)}
                placeholder={t("form.placeholder.email")}
                required
                disabled={loading}
              />
            </div>
            <div>
              <label className="text-sm font-semibold">{t("form.phone")} *</label>
              <input
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className={cls("mt-1 w-full rounded-xl p-3", p.formInput)}
                placeholder={t("form.placeholder.phone")}
                required
                disabled={loading}
              />
            </div>
            <div>
              <label className="text-sm font-semibold">{t("form.company")} *</label>
              <input
                value={company}
                onChange={(e) => setCompany(e.target.value)}
                className={cls("mt-1 w-full rounded-xl p-3", p.formInput)}
                placeholder={t("form.placeholder.company")}
                required
                disabled={loading}
              />
            </div>
            <div className="md:col-span-2">
              <label className="text-sm font-semibold">{t("form.subject")}</label>
              <div className="mt-1 flex gap-2">
                <button
                  type="button"
                  onClick={() => setSubject("presta")}
                  disabled={loading}
                  className={cls(
                    "rounded-xl px-3 py-1.5 text-sm",
                    subject === "presta" ? p.formButtonActive : p.formButtonIdle,
                    loading && "opacity-60 cursor-not-allowed"
                  )}
                >
                  {t("form.subject.presta")}
                </button>
                <button
                  type="button"
                  onClick={() => setSubject("module")}
                  disabled={loading}
                  className={cls(
                    "rounded-xl px-3 py-1.5 text-sm",
                    subject === "module" ? p.formButtonActive : p.formButtonIdle,
                    loading && "opacity-60 cursor-not-allowed"
                  )}
                >
                  {t("form.subject.module")}
                </button>
                <button
                  type="button"
                  onClick={() => setSubject("other")}
                  disabled={loading}
                  className={cls(
                    "rounded-xl px-3 py-1.5 text-sm",
                    subject === "other" ? p.formButtonActive : p.formButtonIdle,
                    loading && "opacity-60 cursor-not-allowed"
                  )}
                >
                  {t("form.subject.other")}
                </button>
              </div>
            </div>
            <div className="md:col-span-2">
              <label className="text-sm font-semibold">{t("form.msg")} *</label>
              <textarea
                value={msg}
                onChange={(e) => setMsg(e.target.value)}
                rows={5}
                className={cls("mt-1 w-full rounded-xl p-3", p.formInput)}
                placeholder={t("form.placeholder.msg")}
                required
                disabled={loading}
              />
            </div>
            <label className="md:col-span-2 inline-flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={consent}
                onChange={(e) => setConsent(e.target.checked)}
                disabled={loading}
              />
              {t("form.consent")}
            </label>

            {success && (
              <div className={cls("md:col-span-2 flex items-center gap-2 rounded-xl p-4 border", p.successMessage)}>
                <CheckCircle size={20} className="flex-shrink-0" />
                <p className="text-sm font-medium">
                  {t("form.success") || "Message sent successfully! We'll get back to you soon."}
                </p>
              </div>
            )}

            {error && (
              <div className={cls("md:col-span-2 flex items-center gap-2 rounded-xl p-4 border", p.errorMessage)}>
                <AlertCircle size={20} className="flex-shrink-0" />
                <p className="text-sm font-medium">{error}</p>
              </div>
            )}

            <div className="md:col-span-2 flex flex-wrap gap-2">
              <button
                type="submit"
                disabled={loading}
                className={cls(
                  "inline-flex items-center gap-2",
                  p.ctaPrimary,
                  loading && "opacity-60 cursor-not-allowed"
                )}
              >
                {loading ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    {t("form.sending") || "Sending..."}
                  </>
                ) : (
                  <>
                    <Send size={16} /> {t("form.send")}
                  </>
                )}
              </button>
              <button
                type="button"
                onClick={() => openWhatsApp("Bonjour, je souhaite un devis.")}
                className={cls("inline-flex items-center gap-2", p.ctaSecondary)}
                disabled={loading}
              >
                <MessageCircleMore size={16} /> WhatsApp
              </button>
            </div>
            <p className={cls("md:col-span-2 text-xs", p.formText)}>
              {t("form.data")}
            </p>
          </form>
        </Container>
      </section>
    </>
  );
}
